import React, { useMemo, useState } from 'react';
import StepLayout from '../components/StepLayout';
import SelectionCard from '../components/SelectionCard';
import { api } from '../api';

// iter-26: rebuild per Final Details Page UI Spec.
//
//   Heading       — "Final details" (sentence case, no asterisk)
//   Subtitle      — "We need a few more details to complete your submission."
//   DOB           — text input, dd/mm/yyyy placeholder, validates format
//                   + 18+, error treatment in red
//   Gender        — single-select cards Male / Female (orange pattern)
//   Continue CTA  — disabled until DOB valid AND gender picked
//   Trustpilot    — kept (StepLayout default)

// iter-34: switched DOB to native <input type="date"> per operator
// request. Browser exposes value as ISO yyyy-mm-dd regardless of
// locale; the visible picker UI is locale-formatted (US mm/dd, UK
// dd/mm) for free. Parser accepts ISO; legacy dd/mm/yyyy strings
// from sessionData (carried over from earlier iters) still parse so
// users coming back to the page mid-flow don't lose their value.
const ISO_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const LEGACY_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;

function parseDob(input) {
  if (!input) return null;
  let day; let month; let year;
  const iso = input.match(ISO_PATTERN);
  if (iso) {
    year = parseInt(iso[1], 10);
    month = parseInt(iso[2], 10);
    day = parseInt(iso[3], 10);
  } else {
    const legacy = input.match(LEGACY_PATTERN);
    if (!legacy) return null;
    day = parseInt(legacy[1], 10);
    month = parseInt(legacy[2], 10);
    year = parseInt(legacy[3], 10);
  }
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  if (year < 1900 || year > new Date().getFullYear()) return null;
  const d = new Date(Date.UTC(year, month - 1, day));
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) {
    return null;
  }
  return d;
}

// Compute the latest valid DOB (today minus 18 years) in ISO format
// for the input's `max` attribute — prevents picking under-18 dates
// in the picker UI before the seller ever submits.
function maxDobIso() {
  const today = new Date();
  const max = new Date(today.getUTCFullYear() - 18, today.getUTCMonth(), today.getUTCDate());
  return max.toISOString().slice(0, 10);
}

function isAtLeast18(date) {
  const today = new Date();
  const eighteenAgo = new Date(today.getUTCFullYear() - 18, today.getUTCMonth(), today.getUTCDate());
  return date.getTime() <= eighteenAgo.getTime();
}

const GENDER_OPTIONS = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
];

export default function Step20FinalDetails({ sessionId, sessionData, updateData, onNext }) {
  const [dob, setDob] = useState(sessionData.dob || '');
  const [gender, setGender] = useState(sessionData.gender || '');
  const [touchedDob, setTouchedDob] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(false);

  const dobError = useMemo(() => {
    if (!touchedDob) return '';
    if (!dob.trim()) return 'Date of birth is required.';
    const parsed = parseDob(dob.trim());
    if (!parsed) return 'Please enter a valid date.';
    if (!isAtLeast18(parsed)) return 'You must be 18 or older to apply.';
    return '';
  }, [dob, touchedDob]);

  const dobIsValid = useMemo(() => {
    const parsed = parseDob(dob.trim());
    return parsed !== null && isAtLeast18(parsed);
  }, [dob]);

  const canSubmit = dobIsValid && !!gender && !loading;

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!touchedDob) setTouchedDob(true);
    if (!canSubmit) return;
    setLoading(true);
    setSubmitError('');
    try {
      // iter-36: BE schema is { dateOfBirth: 'YYYY-MM-DD', gender,
      //                          hasPassport: bool }. The operator
      // reported 'Field required, Field required' which was FastAPI
      // saying both dateOfBirth (FE was sending dob) and hasPassport
      // (FE wasn't sending it at all) were missing. hasPassport is
      // sourced from the earlier Step11 ID Type selection.
      const idTypes = Array.isArray(sessionData?.idTypes) ? sessionData.idTypes : [];
      const hasPassport = idTypes.includes('Passport');
      await api.updateFinalDetails(sessionId, {
        dateOfBirth: dob.trim(),
        gender,
        hasPassport,
      });
      updateData({ dob: dob.trim(), dateOfBirth: dob.trim(), gender, hasPassport });
      onNext();
    } catch (err) {
      setSubmitError(err.message || 'Failed to save.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <StepLayout title="Final details">
      <p style={{
        fontFamily: 'var(--font-sans)',
        fontSize: 16,
        lineHeight: 1.55,
        color: '#444',
        margin: '0 0 32px',
      }}>
        We need a few more details to complete your submission.
      </p>

      <form onSubmit={handleSubmit}>
        {/* Date of birth */}
        <div className="form-field" style={{ maxWidth: 480, marginBottom: 24 }}>
          <label htmlFor="dob" className={`form-field__label${dobError ? ' form-field__label--error' : ''}`}>
            Date of birth <span className="form-field__req">*</span>
          </label>
          <input
            id="dob"
            type="date"
            value={dob}
            max={maxDobIso()}
            min="1900-01-01"
            onChange={(e) => setDob(e.target.value)}
            onBlur={() => setTouchedDob(true)}
            className={`form-field__input dob-input${dobError ? ' dob-input--error' : ''}`}
            aria-required="true"
            aria-invalid={dobError ? 'true' : 'false'}
            autoComplete="bday"
          />
          {dobError && <p className="form-field__error" role="alert">{dobError}</p>}
        </div>

        {/* Gender */}
        <div style={{ maxWidth: 480, marginBottom: 32 }}>
          <div className="form-field__label" style={{ marginBottom: 8 }}>
            Gender <span className="form-field__req">*</span>
          </div>
          <div role="radiogroup" aria-label="Gender" style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {GENDER_OPTIONS.map((opt) => (
              <SelectionCard
                key={opt.value}
                id={`gender-${opt.value.toLowerCase()}`}
                name="gender"
                value={opt.value}
                label={opt.label}
                selected={gender === opt.value}
                onSelect={setGender}
                disabled={loading}
              />
            ))}
          </div>
        </div>

        {submitError && <p className="form-field__error">{submitError}</p>}

        <div className="cta-row">
          <button
            type="submit"
            disabled={!canSubmit}
            aria-disabled={!canSubmit}
            className="cta-row__btn primary-cta final-cta"
          >
            {loading ? 'Saving...' : 'Continue'}
          </button>
        </div>
      </form>
    </StepLayout>
  );
}
